// Libs
import classNames from 'classnames/bind';
import {
  Heading,
  IconButton,
  IconCheckLine,
  IconEditLine,
  IconEyeLine,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, Loading } from '~/components/common';
import BaseDynamicSelect, {
  IBaseSelectPayload,
  IBaseSelectResult,
} from '~/components/common/specific/baseDynamicSelect/BaseDynamicSelect';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { userActions } from '~/thunks/user/userSlice';
import { IStudent } from '~/utils/interface/profile';
import { getStudents, updateUsers } from '~/thunks/user/userThunk';
import { IQueryGetStudent } from '~/utils/interface/account';
import { IBaseSelectOption } from '~/utils/interface/common';
import { getCourses } from '~/thunks/course/courseThunk';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { adminRoute } from '~/utils/routes/admin';
// Styles, images, icons
import styles from './Student.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Student = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/student', 'admin/studentDetail']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { students: studentsResponse, isLoading } = useAppSelector((state) => state.userState);
  //#endregion Selector

  //#region Declare State
  const [studentFocusId, setStudentFocusId] = useState<string>();
  const [students, setStudents] = useState<IStudent[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [courseSelected, setCourseSelected] = useState<string>();
  const [courseSelectedLabel, setCourseSelectedLabel] = useState<string>();
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(false);
  const isAdminRole = localStorage.getItem(StorageEnum.ROLE) === RoleEnum.ADMIN;
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStudents(studentsResponse?.responses ?? []);
  }, [studentsResponse]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const fetchCourseOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      try {
        const resp = await dispatch(getCourses({ page: payload.page, limit: DEFAULT_LIMIT_PAGE })).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch]
  );

  const handleGetStudents = async (query?: IQueryGetStudent) => {
    await dispatch(
      getStudents(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleUpdateStudent = (studentId: string, identificationNumber: string) => {
    setStudentFocusId(undefined);
    dispatch(
      updateUsers({
        users: [
          {
            id: `${studentId}`,
            identificationNumber,
          },
        ],
      })
    )
      .unwrap()
      .then(() => {
        dispatch(userActions.setNewStudents(students));
      });
  };

  const handleViewStudentProgress = (student: IStudent) => {
    navigate(`${adminRoute.base}${adminRoute.students}/${student.id}`, {
      state: {
        studentName: student.fullName,
        studentEmail: student.email,
      },
    });
  };

  const handleClickEdit = (studentId: string) => {
    setStudents(studentsResponse?.responses ?? []);
    setStudentFocusId(studentId);
  };

  const handleChangeValue = (studentId: string, value: string) => {
    setStudents((prevState) =>
      prevState.map((item) => {
        if (studentId === item.id) {
          return {
            ...item,
            identificationNumber: value,
          };
        }
        return item;
      })
    );
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setIsLoadingOverlay(true);
    handleGetStudents({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const renderContent = () => {
    if (isLoading && (!studentsResponse || studentsResponse.responses.length === 0)) {
      return (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (!studentsResponse || studentsResponse.responses.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
        </div>
      );
    }

    return (
      <>
        <View className={cx('studentListContainer')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='Name'>{t('student_name_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='Name'>{t('student_email_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='IdentificationNumber'>
                  {t('student_identification_number_label', { ns: 'admin/student' })}
                </Table.ColHeader>
                <Table.ColHeader id='SendEmail' textAlign='center'>
                  {t('action_label', { ns: 'common/base' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {students.map((data, index) => (
                <Table.Row
                  key={index}
                  className={cx({ clickableRow: isAdminRole })}
                  onClick={() => isAdminRole && handleViewStudentProgress(data)}
                >
                  <Table.Cell>
                    <span>{data.fullName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>{data.email}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span onClick={(e) => e.stopPropagation()}>
                      {data.id === studentFocusId ? (
                        <TextInput
                          type={'tel'}
                          width={'120px'}
                          value={data.identificationNumber}
                          onChange={(e, newValue) => handleChangeValue(data.id, newValue)}
                          renderAfterInput={
                            <IconButton
                              withBorder={false}
                              withBackground={false}
                              color='primary'
                              onClick={() => handleUpdateStudent(data.id, data.identificationNumber ?? '')}
                              screenReaderLabel={'Save'}
                            >
                              {<IconCheckLine />}
                            </IconButton>
                          }
                        />
                      ) : (
                        <>
                          {data.identificationNumber ||
                            t('student_identification_number_not_set', { ns: 'admin/student' })}
                          {isAdminRole && (
                            <IconButton
                              withBorder={false}
                              withBackground={false}
                              color='primary'
                              onClick={() => handleClickEdit(data.id)}
                              screenReaderLabel={'Edit'}
                            >
                              {<IconEditLine />}
                            </IconButton>
                          )}
                        </>
                      )}
                    </span>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <span className={cx('actionCell')} onClick={(e) => e.stopPropagation()}>
                      {data.email ? (
                        <a href={`mailto:${data.email}`} title={t('action_label', { ns: 'common/base' })}>
                          <img
                            src={icons.commonIconSendEmail}
                            alt={t('action_label', { ns: 'common/base' })}
                            className={cx('action')}
                          />
                        </a>
                      ) : (
                        <img
                          src={icons.commonIconSendEmail}
                          alt={t('action_label', { ns: 'common/base' })}
                          className={cx('action')}
                          style={{ opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed' }}
                        />
                      )}
                      {isAdminRole && (
                        <IconButton
                          withBorder={false}
                          withBackground={false}
                          color='secondary'
                          size='small'
                          onClick={() => handleViewStudentProgress(data)}
                          screenReaderLabel={t('student_detail_view_action_label', { ns: 'admin/studentDetail' })}
                          title={t('student_detail_view_action_label', { ns: 'admin/studentDetail' })}
                        >
                          <IconEyeLine />
                        </IconButton>
                      )}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <BasePagination currentPage={page} hasMore={studentsResponse?.hasMore} onChange={handleChangePage} />
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('studentTitle')}>
          <div className={cx('studentHeaderTitle')}>
            <Heading level='h3'>{t('student_label', { ns: 'admin/student' })}</Heading>
            <img
              className={cx('iconHeader')}
              src={icons.commonIconStudent}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </div>

          {isAdminRole && (
            <BaseDynamicSelect
              value={courseSelected}
              selectedLabel={courseSelectedLabel}
              placeholder={t('student_filter_by_course', { ns: 'admin/student' })}
              showDefaultOption={true}
              callApiFn={fetchCourseOptions}
              onChange={(option) => {
                setCourseSelected(option?.value);
                setCourseSelectedLabel(option?.label);
                setPage(DEFAULT_PAGE);
                handleGetStudents({
                  courseId: option?.value,
                  limit: DEFAULT_LIMIT_PAGE,
                  page: DEFAULT_PAGE,
                });
              }}
            />
          )}
        </View>

        {renderContent()}
      </div>

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Student;
